// The motion vocabulary for the admin.
//
// There were six transitions in eighty-three files, every one of them written
// as a bare `120ms` with no easing function named — so they all fell back to
// CSS `ease`, which accelerates into the change and decelerates out of it.
// That curve is right for something travelling between two places on its own
// and wrong for almost everything else: a hover state or a press should leave
// immediately, because the user has already done the thing and is waiting to
// see it acknowledged.
//
// No spring library here on purpose. This is a records product — tables,
// forms, a vault. The one surface with real physical interaction is the
// hiring board's drag, and that is handled with pointer events and a
// transform rather than by adding an animation runtime to the bundle. If a
// genuinely gesture-driven surface shows up later, that is the moment to
// reach for Motion, not before.

/**
 * Durations. Three, because a fourth would only get picked at random.
 *
 * Anything a pointer is directly causing belongs at `instant`. Anything
 * entering or leaving the page belongs at `enter`/`exit`.
 */
export const duration = {
  /** Hover, press, focus — an acknowledgement, not a journey. */
  instant: '100ms',
  /** State changes with somewhere to travel: a row expanding, a chip filling. */
  base: '180ms',
  /** Overlays and panels arriving. */
  enter: '240ms',
} as const;

/**
 * Easings.
 *
 * `standard` decelerates: fast off the mark, settling at the end. It is the
 * right default because it front-loads the information — the user sees where
 * the thing is going almost immediately.
 *
 * `exit` and `enter` are mirror images of each other. A panel that slides in
 * along one curve and leaves along a different one reads as two unrelated
 * events rather than one thing arriving and then going back.
 */
export const easing = {
  standard: 'cubic-bezier(0.2, 0, 0, 1)',
  enter: 'cubic-bezier(0.05, 0.7, 0.1, 1)',
  exit: 'cubic-bezier(0.3, 0, 0.8, 0.15)',
  /**
   * A small overshoot, for the one case it is earned: something the user
   * dropped into place landing. Not for menus, not for hovers.
   */
  overshoot: 'cubic-bezier(0.2, 1.3, 0.4, 1)',
} as const;

/**
 * Build a transition string without inventing new numbers at the call site.
 *
 *   transition: transitions(['border-color', 'box-shadow'])
 *   transition: transitions(['transform'], 'base', 'overshoot')
 */
export function transitions(
  properties: string[],
  d: keyof typeof duration = 'instant',
  e: keyof typeof easing = 'standard'
): string {
  return properties.map((p) => `${p} ${duration[d]} ${easing[e]}`).join(', ');
}

/**
 * The press state every clickable surface should have.
 *
 * The admin had zero `:active` rules. MUI's own `<Button>` has a ripple and
 * was fine; the problem was the hand-rolled clickable `Box`es and `Card`s —
 * stat tiles, folder cards, board columns — which lit up on hover and then
 * did absolutely nothing when clicked. On a touchscreen, where there is no
 * hover at all, that means the control gave no feedback whatsoever until the
 * next page rendered.
 *
 * The dip is small and fast for the same reason it is on mobile: it should be
 * felt rather than watched.
 */
export const pressable = {
  transition: transitions(['transform', 'border-color', 'box-shadow']),
  '&:active': {
    transform: 'scale(0.985)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: transitions(['border-color', 'box-shadow']),
    '&:active': {
      transform: 'none',
    },
  },
} as const;

/**
 * Media query for the reduced-motion preference, for `sx` blocks that need to
 * opt a specific animation out rather than take the global stylesheet rule.
 */
export const reducedMotion = '@media (prefers-reduced-motion: reduce)';

export const motion = { duration, easing, transitions, pressable, reducedMotion } as const;
